import {
  FACTIONS,
  FACTION_ORDER,
  type FactionDefinition,
  type FactionId,
  type FactionSelection
} from '../gameplay/factions';

export interface FactionSelectOverlayCallbacks {
  /** Faction tab clicked. Already filtered against `lockedFactions`. */
  readonly onFactionChange: (id: FactionId) => void;
  readonly onColorChange: (color: number) => void;
  /** "Continue" button — used between the faction stage and the load
   *  game / customise flow that the FactionHub orchestrates. */
  readonly onContinue: () => void;
  /** "Enter sector" button — fired only on the customise stage. */
  readonly onConfirm: () => void;
}

export interface FactionSelectOverlayOptions {
  /** Factions that should be visible but unselectable, with a lock
   *  overlay rendered on top. */
  readonly lockedFactions?: ReadonlyArray<FactionId>;
}

export type FactionSelectStage = 'faction' | 'customize';

/**
 * Faction picker + ship customiser. The same overlay is used for two
 * stages — the FactionHub flips between them via `setStage`. Stage one
 * exposes the faction tabs only ("Continue" CTA); stage two exposes the
 * ship colour palette and the "Enter sector" CTA.
 */
export class FactionSelectOverlay {
  readonly element: HTMLElement;
  private readonly tabs = new Map<FactionId, HTMLButtonElement>();
  private readonly swatches: HTMLButtonElement[] = [];
  private readonly emblemEl: HTMLElement;
  private readonly nameEl: HTMLElement;
  private readonly taglineEl: HTMLElement;
  private readonly descriptionEl: HTMLElement;
  private readonly doctrineEl: HTMLElement;
  private readonly swatchesEl: HTMLElement;
  private readonly continueBtn: HTMLButtonElement;
  private readonly confirmBtn: HTMLButtonElement;
  private readonly colorSection: HTMLElement;
  private readonly subtitleEl: HTMLElement;
  private readonly rootWrap: HTMLElement;
  private readonly lockedFactions: ReadonlySet<FactionId>;
  private current: FactionSelection;
  private stage: FactionSelectStage = 'faction';

  constructor(
    initial: FactionSelection,
    private readonly callbacks: FactionSelectOverlayCallbacks,
    options: FactionSelectOverlayOptions = {}
  ) {
    this.current = initial;
    this.lockedFactions = new Set(options.lockedFactions ?? []);

    this.element = document.createElement('div');
    this.element.className = 'faction-select-overlay';

    const backdrop = document.createElement('div');
    backdrop.className = 'faction-select-backdrop';

    this.rootWrap = document.createElement('div');
    this.rootWrap.className = 'faction-select-root';

    const header = document.createElement('header');
    header.className = 'faction-select-header';
    const titleEl = document.createElement('div');
    titleEl.className = 'faction-select-title';
    titleEl.textContent = 'STARFALL FACTIONS';
    this.subtitleEl = document.createElement('div');
    this.subtitleEl.className = 'faction-select-subtitle';
    this.subtitleEl.textContent = 'Choose your faction';
    header.append(titleEl, this.subtitleEl);
    this.element.dataset['stage'] = 'faction';

    const body = document.createElement('div');
    body.className = 'faction-select-body';

    const tabs = document.createElement('nav');
    tabs.className = 'faction-select-tabs';
    for (const id of FACTION_ORDER) {
      const def = FACTIONS[id];
      const btn = document.createElement('button');
      btn.className = 'faction-select-tab';
      btn.type = 'button';
      btn.dataset['faction'] = id;
      btn.style.setProperty('--tab-color', def.uiColor);
      btn.style.setProperty('--tab-glow', def.uiGlow);
      const locked = this.lockedFactions.has(id);
      if (locked) btn.classList.add('is-locked');
      btn.innerHTML = `
        <div class="faction-tab-emblem">${def.emblem}</div>
        <div class="faction-tab-labels">
          <div class="faction-tab-name">${def.displayName}</div>
          <div class="faction-tab-tagline">${def.tagline}</div>
        </div>
        ${locked ? `
          <div class="faction-tab-lock">
            <div class="faction-tab-lock-icon">${LOCK_ICON}</div>
            <div class="faction-tab-lock-text">Coming Soon</div>
          </div>
        ` : ''}
      `;
      if (!locked) {
        btn.addEventListener('click', () => this.handleFactionClick(id));
      } else {
        btn.disabled = true;
      }
      tabs.appendChild(btn);
      this.tabs.set(id, btn);
    }

    const panel = document.createElement('section');
    panel.className = 'faction-select-panel';

    const panelHead = document.createElement('div');
    panelHead.className = 'faction-panel-head';
    this.emblemEl = document.createElement('div');
    this.emblemEl.className = 'faction-panel-emblem';
    const titleBlock = document.createElement('div');
    titleBlock.className = 'faction-panel-titles';
    this.nameEl = document.createElement('h2');
    this.nameEl.className = 'faction-panel-name';
    this.taglineEl = document.createElement('div');
    this.taglineEl.className = 'faction-panel-tagline';
    titleBlock.append(this.nameEl, this.taglineEl);
    panelHead.append(this.emblemEl, titleBlock);

    this.descriptionEl = document.createElement('p');
    this.descriptionEl.className = 'faction-panel-description';

    this.doctrineEl = document.createElement('ul');
    this.doctrineEl.className = 'faction-panel-doctrine';

    this.colorSection = document.createElement('div');
    this.colorSection.className = 'faction-panel-colors';
    const colorLabel = document.createElement('div');
    colorLabel.className = 'faction-panel-colors-label';
    colorLabel.textContent = 'Ship primary color';
    this.swatchesEl = document.createElement('div');
    this.swatchesEl.className = 'faction-panel-swatches';
    this.colorSection.append(colorLabel, this.swatchesEl);

    const actions = document.createElement('div');
    actions.className = 'faction-panel-actions';

    this.continueBtn = document.createElement('button');
    this.continueBtn.className = 'faction-panel-confirm';
    this.continueBtn.type = 'button';
    this.continueBtn.textContent = 'Continue';
    this.continueBtn.addEventListener('click', () => this.callbacks.onContinue());

    this.confirmBtn = document.createElement('button');
    this.confirmBtn.className = 'faction-panel-confirm';
    this.confirmBtn.type = 'button';
    this.confirmBtn.textContent = 'Enter sector';
    this.confirmBtn.addEventListener('click', () => this.callbacks.onConfirm());

    actions.append(this.continueBtn, this.confirmBtn);

    panel.append(
      panelHead,
      this.descriptionEl,
      this.doctrineEl,
      this.colorSection,
      actions
    );

    body.append(tabs, panel);
    this.rootWrap.append(header, body);
    this.element.append(backdrop, this.rootWrap);

    this.applyFaction(initial.factionId, initial.primaryColor);
    this.applyStage();
  }

  show(): void {
    requestAnimationFrame(() => {
      this.element.classList.add('is-visible');
    });
  }

  hide(): void {
    this.element.classList.remove('is-visible');
  }

  async hideAndDispose(): Promise<void> {
    this.hide();
    await new Promise((resolve) => setTimeout(resolve, 320));
    this.element.remove();
  }

  setStage(stage: FactionSelectStage): void {
    this.stage = stage;
    this.applyStage();
  }

  private applyStage(): void {
    const customizing = this.stage === 'customize';
    this.element.classList.toggle('is-customizing', customizing);
    this.element.dataset['stage'] = customizing ? 'customize' : 'faction';
    this.subtitleEl.textContent = customizing
      ? 'Customise your ship'
      : 'Choose your faction';
    // In customise mode the player has already locked their faction —
    // the doctrine list and large faction title would just repeat what
    // the previous screen already said. Show only the colour picker
    // (and the ship preview behind the overlay) so this stage feels
    // distinct.
    this.colorSection.classList.toggle('is-hidden', !customizing);
    this.continueBtn.classList.toggle('is-hidden', customizing);
    this.confirmBtn.classList.toggle('is-hidden', !customizing);
    this.doctrineEl.classList.toggle('is-hidden', customizing);
    this.descriptionEl.classList.toggle('is-hidden', customizing);
  }

  private handleFactionClick(id: FactionId): void {
    if (this.lockedFactions.has(id)) return;
    if (this.current.factionId === id) return;
    const def = FACTIONS[id];
    this.applyFaction(id, def.defaultColor);
    this.callbacks.onFactionChange(id);
    this.callbacks.onColorChange(def.defaultColor);
  }

  private handleColorClick(color: number): void {
    if (this.current.primaryColor === color) return;
    this.current = { ...this.current, primaryColor: color };
    this.updateSwatchSelection();
    this.callbacks.onColorChange(color);
  }

  private applyFaction(id: FactionId, color: number): void {
    const def = FACTIONS[id];
    this.current = { factionId: id, primaryColor: color };

    for (const [tabId, btn] of this.tabs.entries()) {
      btn.classList.toggle('is-active', tabId === id);
    }

    this.emblemEl.innerHTML = def.emblem;
    this.nameEl.textContent = def.displayName;
    this.taglineEl.textContent = def.tagline;
    this.descriptionEl.textContent = def.description;

    this.doctrineEl.innerHTML = '';
    for (const line of def.doctrine) {
      const li = document.createElement('li');
      li.textContent = line;
      this.doctrineEl.appendChild(li);
    }

    this.rootWrap.style.setProperty('--accent', def.uiColor);
    this.rootWrap.style.setProperty('--accent-glow', def.uiGlow);

    this.buildSwatches(def);
    this.updateSwatchSelection();
  }

  private buildSwatches(def: FactionDefinition): void {
    this.swatchesEl.innerHTML = '';
    this.swatches.length = 0;
    for (const color of def.colorChoices) {
      const btn = document.createElement('button');
      btn.className = 'faction-swatch';
      btn.type = 'button';
      btn.dataset['color'] = String(color);
      btn.style.setProperty('--swatch', toCssHex(color));
      btn.setAttribute('aria-label', `Color ${toCssHex(color)}`);
      btn.addEventListener('click', () => this.handleColorClick(color));
      this.swatchesEl.appendChild(btn);
      this.swatches.push(btn);
    }
  }

  private updateSwatchSelection(): void {
    for (const btn of this.swatches) {
      const color = Number(btn.dataset['color']);
      btn.classList.toggle('is-active', color === this.current.primaryColor);
    }
  }
}

const LOCK_ICON = `
<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
  <rect x="4.5" y="10" width="15" height="11" rx="2" stroke="currentColor" stroke-width="2" />
  <path d="M8 10V7a4 4 0 1 1 8 0v3" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
  <circle cx="12" cy="15.5" r="1.6" fill="currentColor" />
</svg>`;

function toCssHex(color: number): string {
  return `#${color.toString(16).padStart(6, '0')}`;
}
